// Tipli SEROTA API istemcisi (web paneller ve Expo uygulamaları ortak).
// Erişim belirteci süresi dolunca yenileme belirteciyle bir kez otomatik yenilenir; başarısızsa onUnauthorized.
import type { ClientApp, MeContext, OtpVerifyResponse, TokenPair, User } from '@serota/domain';

export interface TokenStore {
  get():
    | Promise<{ accessToken: string; refreshToken: string } | null>
    | { accessToken: string; refreshToken: string }
    | null;
  set(tokens: { accessToken: string; refreshToken: string } | null): Promise<void> | void;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  app: ClientApp;
  tokens: TokenStore;
  getCompanyId?: () => string | null;
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
}

type Query = Record<string, string | number | boolean | null | undefined>;

export class ApiClient {
  private refreshing: Promise<boolean> | null = null;

  constructor(private opts: ApiClientOptions) {}

  get baseUrl(): string {
    return this.opts.baseUrl;
  }

  private async headers(extra?: HeadersInit): Promise<Headers> {
    const h = new Headers(extra);
    const t = await this.opts.tokens.get();
    if (t?.accessToken) h.set('authorization', `Bearer ${t.accessToken}`);
    const company = this.opts.getCompanyId?.();
    if (company) h.set('x-company-id', company);
    return h;
  }

  private async tryRefresh(): Promise<boolean> {
    if (this.refreshing) return this.refreshing;
    this.refreshing = (async () => {
      const t = await this.opts.tokens.get();
      if (!t?.refreshToken) return false;
      const f = this.opts.fetchImpl ?? fetch;
      const res = await f(`${this.opts.baseUrl}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: t.refreshToken }),
      }).catch(() => null);
      if (!res || !res.ok) {
        await this.opts.tokens.set(null);
        return false;
      }
      const pair = (await res.json()) as TokenPair;
      await this.opts.tokens.set({ accessToken: pair.accessToken, refreshToken: pair.refreshToken });
      return true;
    })();
    try {
      return await this.refreshing;
    } finally {
      this.refreshing = null;
    }
  }

  async request<T>(
    method: string,
    path: string,
    opts: { body?: unknown; query?: Query; form?: FormData; retry?: boolean } = {},
  ): Promise<T> {
    const f = this.opts.fetchImpl ?? fetch;
    const qs = opts.query
      ? '?' +
        Object.entries(opts.query)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    const headers = await this.headers(
      opts.form ? undefined : opts.body !== undefined ? { 'content-type': 'application/json' } : undefined,
    );
    let res: Response;
    try {
      res = await f(`${this.opts.baseUrl}${path}${qs === '?' ? '' : qs}`, {
        method,
        headers,
        body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
      });
    } catch {
      throw new ApiError(0, 'NETWORK', 'İnternet bağlantısı yok. Bağlantı gelince otomatik güncellenir.');
    }
    if (res.status === 401 && opts.retry !== false && !path.startsWith('/v1/auth/')) {
      if (await this.tryRefresh()) return this.request<T>(method, path, { ...opts, retry: false });
      this.opts.onUnauthorized?.();
    }
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as {
        error?: { code: string; message: string; details?: unknown };
      } | null;
      throw new ApiError(
        res.status,
        body?.error?.code ?? 'HTTP',
        body?.error?.message ?? 'Bir sorun oluştu. Birazdan tekrar deneyin.',
        body?.error?.details,
      );
    }
    if (res.status === 204) return undefined as T;
    const ct = res.headers.get('content-type') ?? '';
    return (ct.includes('application/json') ? await res.json() : await res.blob()) as T;
  }

  get<T>(path: string, query?: Query) {
    return this.request<T>('GET', path, { query });
  }
  post<T>(path: string, body?: unknown) {
    return this.request<T>('POST', path, { body: body ?? {} });
  }
  put<T>(path: string, body?: unknown) {
    return this.request<T>('PUT', path, { body: body ?? {} });
  }
  patch<T>(path: string, body?: unknown) {
    return this.request<T>('PATCH', path, { body: body ?? {} });
  }
  del<T>(path: string) {
    return this.request<T>('DELETE', path);
  }
  upload<T>(path: string, form: FormData) {
    return this.request<T>('POST', path, { form });
  }

  // ───────── kimlik ─────────
  requestOtp(phone: string) {
    return this.post<{ ok: true; expiresInS: number; resendInS: number }>('/v1/auth/otp/request', {
      phone,
      app: this.opts.app,
    });
  }
  async verifyOtp(phone: string, code: string, device?: string) {
    const r = await this.post<OtpVerifyResponse>('/v1/auth/otp/verify', { phone, code, app: this.opts.app, device });
    await this.opts.tokens.set({ accessToken: r.accessToken, refreshToken: r.refreshToken });
    return r;
  }
  async logout() {
    await this.post('/v1/auth/logout').catch(() => undefined);
    await this.opts.tokens.set(null);
  }
  me() {
    return this.get<User>('/v1/me');
  }
  updateMe(body: { firstName?: string; lastName?: string; email?: string }) {
    return this.patch<User>('/v1/me', body);
  }
  context() {
    return this.get<MeContext>('/v1/me/context');
  }
  setAppRole(role: 'SELF' | 'CHILD' | 'BOTH' | 'DRIVER' | 'GUIDE') {
    return this.put('/v1/me/app-role', { role });
  }
}

/** Tarayıcı için localStorage tabanlı belirteç deposu (yalnız kolaylık; erişilemezse bellekte tutar). */
export function browserTokenStore(key = 'serota.tokens'): TokenStore {
  let mem: { accessToken: string; refreshToken: string } | null = null;
  return {
    get() {
      try {
        const v = localStorage.getItem(key);
        return v ? JSON.parse(v) : mem;
      } catch {
        return mem;
      }
    },
    set(t) {
      mem = t;
      try {
        if (t) localStorage.setItem(key, JSON.stringify(t));
        else localStorage.removeItem(key);
      } catch {
        /* özel pencere vb. */
      }
    },
  };
}
